import {useEffect} from "react";
import {Page} from "../../../../misc/pdf/parts/Page";
import {PDFHeader} from "../../../../misc/pdf/parts/PDFHeader";
import {GlazingSection, GlazingSectionBody} from "../../../project/shopdrawing/glazingSubmittal/GlazingSectionPage";
import {
    GlazingSummaryRow,
    GlazingSummaryTable,
    summarizeGlass
} from "../../../project/shopdrawing/glazingSubmittal/GlazingSummary";
import {PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {GlazingCustomItem} from "../../../../api/ElevationTool";
import {useSyncedRef} from "../../../../misc/SyncedRef";

// the glass on a purchase order, one page per elevation variant: the drawing it was measured on,
// the glass table and the openings that take it. Same content as the glazing submittal, under
// the purchase order's page header rather than the project submittal's.
export type GlazingDetail = {
    screens: GlazingSection[];
    doors: GlazingSection[];
    // glass with no elevation of its own, listed in the summary only
    custom: GlazingCustomItem[];
};

export function GlazingDetails(props: {
    value: GlazingDetail;
    onReady(tf: boolean): void;
}) {
    // a purchase order can span projects, so the heading names the project when more than one
    // section's glass is on the order.
    const sections = [...props.value.screens, ...props.value.doors];
    const multiProject = new Set(sections.map(s => s.projectName)).size > 1;

    const heading = (section: GlazingSection) =>
        multiProject ? section.projectName + " - " + section.heading : section.heading;

    const summary = summarizeGlass(sections, props.value.custom);

    // the summary page reports when it has laid out; with nothing to summarize there is no page to
    // wait for, so report on its behalf
    const empty = summary.length === 0;
    const onReadyRef = useSyncedRef(props.onReady);
    useEffect(() => {
        if(empty) onReadyRef.current(true);
    }, [empty, onReadyRef]);

    return (
        <>
            {summary.length > 0 && <GlazingSummarySection rows={summary} onReady={props.onReady} />}
            {props.value.screens.map((s, index) => (
                <Page orientation="landscape" key={"screen-" + index}>
                    <div style={{flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch"}}>
                        <PDFHeader noAddress title="Frame Glazing" />
                        <GlazingSectionBody section={s} heading={heading(s)} />
                    </div>
                </Page>
            ))}
            {props.value.doors.map((s, index) => (
                <Page orientation="landscape" key={"door-" + index}>
                    <div style={{flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch"}}>
                        <PDFHeader noAddress title="Door Glazing" />
                        <GlazingSectionBody section={s} heading={heading(s)} />
                    </div>
                </Page>
            ))}
        </>
    );
}

// the glass the order asks for, rolled up by size/type/thickness. One-off glass has no elevation
// section of its own, so this is the only place it prints.
function GlazingSummarySection(props: {
    rows: GlazingSummaryRow[];
    onReady(tf: boolean): void;
}) {
    return (
        <PageRenderProvider data={props.rows} onReady={props.onReady} debugKey="po-glazing-summary">
            {info =>
                <Page>
                    <div style={{flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch"}}>
                        <PDFHeader noAddress title="Glass Summary" />
                        <GlazingSummaryTable rows={info.rows} />
                    </div>
                </Page>}
        </PageRenderProvider>
    );
}
